import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router-dom";
import "./SaleOrder.css";
import Header from "./Header";
import Footer from "./Footer";
import moment from "moment";
import { baseURL } from "./api";
import BillBreakdown from "./BillBreakdown";
import BillBreakdownOrder from "./BillBreakdownOrder";

const trackingSteps = [
  { label: "Booked",     status: "booked",     icon: "fas fa-cart-plus" },
  { label: "Accepted",   status: "accepted",   icon: "fas fa-thumbs-up" },
  { label: "Confirmed",  status: "confirmed",  icon: "fas fa-check-circle" },
  { label: "Processing", status: "processing", icon: "fas fa-concierge-bell" },
  { label: "Packed",     status: "packed",     icon: "fas fa-box" },
  { label: "Shipped",    status: "shipped",    icon: "fas fa-truck" },
  { label: "Delivered",  status: "delivered",  icon: "fas fa-clipboard-check" },
  { label: "Received",   status: "received",   icon: "fas fa-check-circle" },
];

const getCurrentStep = (status) => {
  const idx = trackingSteps.findIndex((s) => s.status === status);
  return idx === -1 ? 0 : idx;
};

const SaleOrder = () => {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receivedOrders, setReceivedOrders] = useState({});
  const [qtyUpdated, setQtyUpdated] = useState({});
  const [priceType, setPriceType] = useState(localStorage.getItem("priceType") || "purchase");
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantMap, setRestaurantMap] = useState({});
  

  // ✅ Breakdown modal control
  const [breakdownOpenFor, setBreakdownOpenFor] = useState(null);

  const handleReceived = async (order) => {
    try {
      const updatedOrder = { ...order, status: "received" };
      await axios.put(`${baseURL}/updateOrder/${order.orderNumber}`, updatedOrder);

      if (order.priceType !== "sale") {
        const payloads = order.cart.map((item) => ({
          vendorId: item.vendorId,
          restaurantId: item.restaurantId,
          menuItemId: item._id,
          quantity: item.quantity,
        }));
        await Promise.all(payloads.map((p) => axios.post(`${baseURL}/addVendorInventory`, p)));
      }

      setOrders((prev) =>
        prev.map((o) =>
          o.orderNumber === order.orderNumber
            ? { ...o, status: "received", currentStep: getCurrentStep("received") }
            : o
        )
      );
      setReceivedOrders((p) => ({ ...p, [order.orderNumber]: true }));
      setQtyUpdated((p) => ({ ...p, [order.orderNumber]: true }));
    } catch (err) {
      console.error("Order update failed:", err);
      alert("Error updating order status. Please try again.");
    }
  };

  useEffect(() => {
    const vendorId = localStorage.getItem("vendorId");
    const fetchAll = async () => {
      if (!vendorId) {
        setError("Vendor ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const [orderRes, restRes] = await Promise.all([
          axios.get(`${baseURL}/getOrder?vendorId=${vendorId}`),
          axios.get(`${baseURL}/getRestaurant`),
        ]);

        const restList = restRes.data;
        const map = {};
        restList.forEach((r) => (map[r._id] = r.default === true));

        setRestaurants(restList);
        setRestaurantMap(map);

        const filtered = orderRes.data
          .filter((o) => o.priceType === priceType)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map((o) => {
            const rId = o.cart[0]?.restaurantId;
            return {
              ...o,
              formattedOrderDate: moment(o.date).format("MMMM DD, YYYY, h:mm A"),
              currentStep: getCurrentStep(o.status),
              showTracking: map[rId] === true,
            };
          });

        setOrders(filtered);
      } catch (e) {
        console.error(e);
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [priceType]);

  const isSale = priceType === "sale";
  const primary = isSale ? "#1976d2" : "#ff1547";
  const bg = isSale ? "#e3f2fd" : "#ffe6ec";
  const heading = isSale ? "#0d47a1" : "#b3002d";
  const darkText = primary;

  if (loading) return <div>Loading...</div>;

  return (
    <div className="sale-order" style={{ backgroundColor: isSale ? "#f5f9ff" : "#fff5f5" }}>
      <Header />
      <h2 style={{ color: primary }}>
        {isSale ? "Sale Order Status" : "Purchase Order Status"}
      </h2>

      <div className="button-container">
        <button
          className="back"
          onClick={() => (window.location.href = `/home?priceType=${priceType}`)}
          style={{ backgroundColor: primary, color: "white" }}
        >
          Back to Home
        </button>
      </div>

      {error || orders.length === 0 ? (
        <div className="no-orders">
          <h3 style={{ color: heading }}>You have no {isSale ? "Sale" : "Purchase"} Orders</h3>
          <p>
            There are no {isSale ? "sale" : "purchase"} orders associated with your account at the
            moment.
          </p>
        </div>
      ) : (
        orders.map((order) => {
          const {
            cart = [],
            subtotal = 0,
            total = 0,
            orderNumber,
            formattedOrderDate,
            currentStep = 0,
          } = order;

          return (
            <div
              key={orderNumber}
              className="order-summary"
              style={{
                borderLeft: `5px solid ${primary}`,
                backgroundColor: bg,
                padding: "10px",
                marginBottom: "15px",
              }}
            >
              <h3 style={{ color: heading }}>
                Order Number: {orderNumber}
                {order.priceType === "purchase" && (
                  <span style={{ fontSize: 14, color: "#555" }}>
                    {" "}
                    -{" "}
                    {restaurants.find((r) => r._id === order.cart[0]?.restaurantId)?.name ||
                      "Unknown Restaurant"}
                  </span>
                )}
              </h3>

              <p>Order Date: {formattedOrderDate}</p>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <h3 style={{ color: darkText }}>Order Summary</h3>
                </AccordionSummary>

                <AccordionDetails>
                  <div
                    className="order-details"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div className="order-items">
                      {cart.length ? (
                        (() => {
                          const lastSnap =
                            order.cartHistory?.length > 0
                              ? order.cartHistory.at(-1).cartSnapshot
                              : [];
                          const prevQtyMap = {};
                          lastSnap.forEach((i) => (prevQtyMap[i._id] = i.quantity));

                          return cart.map((item, idx) => {
                            const price = parseFloat(item.price || 0);
                            const totalItem = price * (item.quantity || 1);
                            const prevQty = item.previousQty ?? prevQtyMap[item._id];
                            const qtyChanged = prevQty != null && prevQty !== item.quantity;

                            return (
                              <div key={idx} className="order-item" style={{ marginBottom: 10 }}>
                                <div
                                  className="order-item-info"
                                  style={{ display: "flex", gap: 10 }}
                                >
                                  <img
                                    src={
                                      item.imgPath
                                        ? `${baseURL}/${item.imgPath}`
                                        : "/path/to/default-image.jpg"
                                    }
                                    alt={item.name}
                                    style={{ width: 60, height: 60, borderRadius: 8 }}
                                    onError={(e) =>
                                      (e.target.src = "/path/to/default-image.jpg")
                                    }
                                  />
                                  <div className="item-line">
                                    <h4>{item.name}</h4>
                                    <p style={{ margin: 0 }}>
                                      {item.quantity} × ₹{price} = ₹{totalItem.toFixed(2)}
                                    </p>
                                    {qtyChanged && (
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#d32f2f",
                                          marginLeft: 2,
                                        }}
                                      >
                                        (Quantity revised, previous qty = {prevQty})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()
                      ) : (
                        <p>No items found in your order.</p>
                      )}
                    </div>

                    <div className="order-summary-right" style={{ textAlign: "right" }}>
  <h3 style={{ color: heading }}>
  Ex‑Factory Total: ₹{Number(order.total || 0).toFixed(2)}
</h3>

  <button
    className="small-btn"
    onClick={() => setBreakdownOpenFor(orderNumber)} // ✅ open modal here
    style={{ marginTop: 8 }}
  >
    View Breakdown
  </button>
</div>

                  </div>
                </AccordionDetails>

                {!isSale && order.showTracking && (
                  <AccordionDetails>
                    <div className="order-tracking">
                      <h3 className="trackhead">Track Your Order</h3>
                      <div className="stepper-wrapper">
                        <Stepper activeStep={currentStep} alternativeLabel>
                          {trackingSteps.map((step, idx) => (
                            <Step key={idx} completed={idx <= currentStep}>
                              <StepLabel
                                StepIconComponent={() => (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      color: idx <= currentStep ? "#388e3c" : "#ccc",
                                    }}
                                  >
                                    <i
                                      className={step.icon}
                                      style={{
                                        fontSize: 22,
                                        color: idx <= currentStep ? "#388e3c" : "#ccc",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: 10,
                                        marginTop: 2,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {idx <= currentStep ? "Done" : "Pending"}
                                    </span>
                                  </div>
                                )}
                              >
                                <span className="step-label">{step.label}</span>
                              </StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      </div>
                    </div>
                  </AccordionDetails>
                )}

                <AccordionDetails>
                  <div className="order-footer">
                    {order.priceType === "sale" ? (
                      !receivedOrders[orderNumber] && (
                        <>
                          <button
                            className="received-button"
                            onClick={async () => {
                              if (receivedOrders[orderNumber]) return;
                              try {
                                await Promise.all(
                                  order.cart.map((item) =>
                                    axios.post(`${baseURL}/reduceVendorInventory`, {
                                      vendorId: item.vendorId,
                                      restaurantId: item.restaurantId,
                                      itemId: item._id,
                                      quantity: item.quantity,
                                    })
                                  )
                                );
                                setQtyUpdated((p) => ({ ...p, [orderNumber]: true }));
                                handleReceived(order);
                              } catch (err) {
                                console.error("Failed to reduce inventory:", err);
                              }
                            }}
                            disabled={
                              receivedOrders[orderNumber] || order.status === "received"
                            }
                            style={{
                              backgroundColor:
                                receivedOrders[orderNumber] || order.status === "received"
                                  ? "#ccc"
                                  : primary,
                              color: "white",
                              padding: "10px 20px",
                              border: "none",
                              cursor:
                                receivedOrders[orderNumber] || order.status === "received"
                                  ? "default"
                                  : "pointer",
                              borderRadius: 5,
                            }}
                          >
                            {receivedOrders[orderNumber] || order.status === "booked"
                              ? "Confirm Order ?"
                              : "Confirmed ✅"}
                          </button>
                          {qtyUpdated[orderNumber] && (
                            <span
                              style={{
                                marginLeft: 12,
                                color: "#388e3c",
                                fontWeight: 600,
                              }}
                            >
                              Quantity updated ✅
                            </span>
                          )}
                        </>
                      )
                    ) : (
                      ( order.status === "delivered" &&
  !receivedOrders[orderNumber]) && (
                        <>
                          <button
                            className="received-button"
                            onClick={() => {
                              if (
                                receivedOrders[orderNumber] ||
                                order.status === "received"
                              )
                                return;
                              handleReceived(order);
                            }}
                            disabled={
                              receivedOrders[orderNumber] ||
                              order.status === "received"
                            }
                            style={{
                              backgroundColor:
                                receivedOrders[orderNumber] ||
                                order.status === "received"
                                  ? "#ccc"
                                  : primary,
                              color: "white",
                              padding: "10px 20px",
                              border: "none",
                              cursor:
                                receivedOrders[orderNumber] ||
                                order.status === "received"
                                  ? "default"
                                  : "pointer",
                              borderRadius: 5,
                            }}
                          >
                            {receivedOrders[orderNumber] || order.status === "received"
                              ? "Received ✅"
                              : "Mark as Received"}
                          </button>
                          {qtyUpdated[orderNumber] && (
                            <span
                              style={{
                                marginLeft: 12,
                                color: "#388e3c",
                                fontWeight: 600,
                              }}
                            >
                              Quantity updated ✅
                            </span>
                          )}
                        </>
                      )
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          );
        })
      )}

      {/* ✅ Shared Bill Breakdown modal */}
      <BillBreakdownOrder
        open={Boolean(breakdownOpenFor)}
        onClose={() => setBreakdownOpenFor(null)}
        order={
          breakdownOpenFor
            ? orders.find((o) => o.orderNumber === breakdownOpenFor)
            : null
        }
      />


      <Footer />
    </div>
  );
};

export default SaleOrder;
